import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { exchangeCode } from "../../auth/cognito";
import { completeSignIn } from "../../auth/session";
import {
  clearGoogleRedirectState,
  getGoogleAuthorizeUrl,
  loadGoogleRedirectState,
  saveGoogleRedirectState,
} from "../../auth/googleRedirect";
import { useAppDispatch } from "../../hooks";
import { signedIn, signedOut } from "../../store/auth/authSlice";
import { useRestartProblem, useStartProblem } from "../problems/useStartProblem";
import { trackEvent, GA_EVENTS } from "../../utils/analytics";
import { COLOR_BG, COLOR_PRIMARY, COLOR_TEXT_MUTED } from "../../styles/colors";

/** Cognito's known first-link failure; retrying the sign-in succeeds. */
const FIRST_LINK_ERROR = "Already found an entry for username";

interface AuthCallbackPageProps {
  /** Where to send the browser to retry (tests replace it). */
  redirect?: (url: string) => void;
}

/** Finishes Google sign-in: Cognito redirects here with ?code=… (or ?error=…). */
export const AuthCallbackPage: React.FC<AuthCallbackPageProps> = ({
  redirect = (url) => window.location.assign(url),
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const startProblem = useStartProblem();
  const restartProblem = useRestartProblem();
  const [error, setError] = useState<string | null>(null);
  const handledRef = useRef(false);

  useEffect(() => {
    // The code is single-use, so StrictMode's second effect run must not exchange it again
    if (handledRef.current) return;
    handledRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const saved = loadGoogleRedirectState();

    const fail = (message: string) => {
      clearGoogleRedirectState();
      dispatch(signedOut());
      setError(message);
    };

    const finish = async () => {
      const errorDescription = params.get("error_description") ?? params.get("error");
      if (errorDescription) {
        if (saved && !saved.retried && errorDescription.includes(FIRST_LINK_ERROR)) {
          saveGoogleRedirectState({ ...saved, retried: true });
          redirect(await getGoogleAuthorizeUrl(saved));
          return;
        }
        fail("Google sign-in didn't go through. Please try again.");
        return;
      }

      const code = params.get("code");
      if (!code || !saved || params.get("state") !== saved.state) {
        fail("This sign-in link has expired. Please try again.");
        return;
      }

      try {
        const profile = await completeSignIn(await exchangeCode(code, saved.codeVerifier));
        clearGoogleRedirectState();
        dispatch(signedIn({ email: profile?.email, name: profile?.name }));
        trackEvent(GA_EVENTS.SIGN_IN, { method: "google" });
        if (saved.pendingProblem) {
          const { problemId, restart } = saved.pendingProblem;
          if (restart) restartProblem(problemId);
          else startProblem(problemId);
          navigate("/app", { replace: true });
        } else {
          navigate(saved.returnPath || "/", { replace: true });
        }
      } catch {
        fail("Google sign-in didn't go through. Please try again.");
      }
    };
    void finish();
  }, [dispatch, navigate, redirect, restartProblem, startProblem]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        backgroundColor: COLOR_BG,
        px: 2,
        textAlign: "center",
      }}
    >
      {error ? (
        <>
          <Typography variant="h6" sx={{ color: COLOR_PRIMARY, fontWeight: 700 }} data-testid="auth-callback-error">
            {error}
          </Typography>
          <Button variant="contained" onClick={() => navigate("/app", { replace: true })} sx={{ textTransform: "none" }}>
            Back to the playground
          </Button>
        </>
      ) : (
        <>
          <CircularProgress />
          <Typography sx={{ color: COLOR_TEXT_MUTED }}>Signing you in…</Typography>
        </>
      )}
    </Box>
  );
};
